// src/components/SidebarLayout.tsx
import React, { ReactNode, useState, useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import { useUser } from '../contexts/UserContext'
import api from '../services/api'
import './SidebarLayout.css'
import {
    HomeOutlined,
    FileTextOutlined,
    TeamOutlined,
    FileOutlined,
    QuestionCircleOutlined,
    FileSearchOutlined,
    DoubleRightOutlined,
    DoubleLeftOutlined,
    ShoppingCartOutlined,
    CreditCardOutlined,
    GlobalOutlined,
    ApiOutlined,
    DatabaseOutlined,
} from '@ant-design/icons'

interface SubscriptionStatus {
    plan: any;
    trial_used: boolean;
    status?: string;
    grace_period_end?: string;
    has_active_api_subscription?: boolean;
}

interface NavItem {
    to: string;
    icon: React.ReactElement;
    label: string;
    requireAdmin?: boolean;
    requireSuperAdmin?: boolean;
    requireNotSuperAdmin?: boolean;
    requireStandardUser?: boolean;
    freshUserOnly?: boolean;
}

interface SidebarLayoutProps {
    children: ReactNode
}

const SidebarLayout: React.FC<SidebarLayoutProps> = ({ children }) => {
    const { isAdmin, isSuperAdmin, isExternalApiUser, loading } = useUser()
    const [isCollapsed, setIsCollapsed] = useState(true) // Default to collapsed
    const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null)
    const [subscriptionLoading, setSubscriptionLoading] = useState(true)

    // Fetch subscription status (only for admin users)
    useEffect(() => {
        const fetchSubscriptionStatus = async () => {
            // Only fetch subscription status for admin users
            // Super admins don't need subscription checking
            if (!isAdmin() || isSuperAdmin()) {
                setSubscriptionLoading(false)
                return
            }

            try {
                setSubscriptionLoading(true)
                const response = await api.get('subscriptions/me/')
                setSubscriptionStatus(response.data)
            } catch (error) {
                console.error('Error fetching subscription status:', error)
                setSubscriptionStatus(null)
            } finally {
                setSubscriptionLoading(false)
            }
        }

        fetchSubscriptionStatus()
    }, [isAdmin, isSuperAdmin])

    // Check if user is fresh (no subscription history)
    const isFreshUser = () => {
        // Super admins are never considered "fresh" - they get full navigation
        if (isSuperAdmin()) return false

        if (subscriptionLoading || !subscriptionStatus) return false

        // An active API-plan subscription disqualifies the user from being
        // "fresh" — they're a paying integration partner and need their
        // own scoped sidebar (handled by isApiOnlyCustomer below), not the
        // empty "Get Started" experience.
        if (subscriptionStatus.has_active_api_subscription) return false

        return subscriptionStatus.plan === null && !subscriptionStatus.trial_used
    }

    // API-only customer: holds an active Connect / Connect Pro plan but
    // has never held a B2C plan. They should see ONLY the menu items
    // that matter to an integration partner — API Key, Subscriptions,
    // Help — not the B2C-flavoured pages (Dashboard, Assessments,
    // Business Assessment, View Candidates, Talent Pool) which require
    // a website subscription to be useful.
    const isApiOnlyCustomer = () => {
        if (isSuperAdmin()) return false
        if (subscriptionLoading || !subscriptionStatus) return false
        return !!subscriptionStatus.has_active_api_subscription
            && subscriptionStatus.plan == null
    }

    // Generate dynamic nav items based on subscription status
    const getDynamicNavItems = (): NavItem[] => {
        const baseNavItems: NavItem[] = [
            //Candidate
            { to: '/test-dashboard', icon: <HomeOutlined />, label: 'Dashboard - Quick overview/home screen', requireStandardUser: true },
            { to: '/browse-tests', icon: <ShoppingCartOutlined />, label: 'Assessment Library - Browse assessments', requireStandardUser: true },
            { to: '/help', icon: <QuestionCircleOutlined />, label: 'Help & Support - FAQs, chat, tutorials / whatever resources we provide', requireStandardUser: true },
            //Business & Super admin
            { to: '/dashboard', icon: <HomeOutlined />, label: 'Dashboard - Quick overview/home screen', requireAdmin: true },
            { to: '/industries-list', icon: <FileTextOutlined />, label: 'Assessments - Create/view/manage assessments', requireSuperAdmin: true },
            { to: '/business-assessment', icon: <FileSearchOutlined />, label: 'Business Assessment - Manage and assign assessments to candidates', requireAdmin: true, requireNotSuperAdmin: true },
            { to: '/view-candidates', icon: <TeamOutlined />, label: 'My Applicants - Manage candidates who applied to your jobs', requireAdmin: true },
            { to: '/talent-pool/start', icon: <GlobalOutlined />, label: 'Talent Pool - Browse all available candidates', requireAdmin: true },
            { to: '/api-key-settings', icon: <ApiOutlined />, label: 'API Key - Manage your API integration key', requireAdmin: true, requireNotSuperAdmin: true },
            { to: '/manage/b2b-sessions', icon: <DatabaseOutlined />, label: 'B2B Sessions - Browse all external API test sessions', requireSuperAdmin: true },
            { to: '/manage/api-clients', icon: <ApiOutlined />, label: 'API Clients - Manage integration partners, suspend access', requireSuperAdmin: true },
            { to: '/manage/api-subscriptions', icon: <CreditCardOutlined />, label: 'API Subscriptions - Invite businesses to Connect plans, track invitations', requireSuperAdmin: true },
            { to: '/help', icon: <QuestionCircleOutlined />, label: 'Help & Support - FAQs, chat, tutorials / whatever resources we provide', requireAdmin: true },
        ]

        // For fresh users (admin without subscription history), show only Help and Subscriptions
        // Super admins bypass this restriction and always get full navigation
        if (isAdmin() && !isSuperAdmin() && isFreshUser()) {
            return [
                { to: '/subscriptions', icon: <CreditCardOutlined />, label: 'Subscriptions - Manage your subscription plan', requireAdmin: true, freshUserOnly: true },
                { to: '/help', icon: <QuestionCircleOutlined />, label: 'Help & Support - FAQs, chat, tutorials / whatever resources we provide', requireAdmin: true },
            ]
        }

        // API-only customer (Connect / Connect Pro, no B2C plan):
        // scoped to integration-relevant pages only. Adding the B2C
        // pages would just confuse them since they're not paying for
        // that side of the platform.
        if (isAdmin() && !isSuperAdmin() && isApiOnlyCustomer()) {
            return [
                { to: '/api-key-settings', icon: <ApiOutlined />, label: 'API Key - Manage your API integration key', requireAdmin: true, requireNotSuperAdmin: true },
                { to: '/subscriptions', icon: <CreditCardOutlined />, label: 'Subscriptions - Manage your subscription plan', requireAdmin: true },
                { to: '/help', icon: <QuestionCircleOutlined />, label: 'Help & Support - FAQs, chat, tutorials / whatever resources we provide', requireAdmin: true },
            ]
        }

        // For users with subscription history, return normal nav items (no subscription menu)
        return baseNavItems
    }

    if (loading || subscriptionLoading) {
        return <div>Loading...</div>
    }

    // B2B external API users see no sidebar chrome. They only have access to the
    // test-taking flow and any extra navigation would be broken (RequiredAuth
    // would bounce them anyway).
    if (isExternalApiUser()) {
        return <>{children}</>
    }

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed)
    }

    const navItems = getDynamicNavItems()

    return (
        <>
            <aside className={`sidebar ${isCollapsed ? 'collapsed' : 'expanded'}`}>
                <div className="sidebar-content">
                    {navItems.map((item) => {
                        const { to, icon, label, requireAdmin, requireSuperAdmin, requireNotSuperAdmin, requireStandardUser, freshUserOnly } = item;

                        // Hide standard user items from admin users
                        if (requireStandardUser && isAdmin()) {
                            return null
                        }

                        // Hide admin-only items from standard users
                        if (requireAdmin && !isAdmin()) {
                            return null
                        }

                        if (requireSuperAdmin && !isSuperAdmin()) {
                            return null
                        }

                        // Hide admin-only (not super admin) items from super admin users
                        if (requireNotSuperAdmin && isSuperAdmin()) {
                            return null
                        }

                        // Handle fresh user only items
                        if (freshUserOnly && !isFreshUser()) {
                            return null
                        }

                        return (
                            <div key={to} className="nav-item">
                                <NavLink
                                    to={to}
                                    title={isCollapsed ? label : undefined}
                                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''} ${to === '' ? 'disabled' : ''}`}
                                >
                                    <span className="nav-icon">{icon}</span>
                                    {!isCollapsed && <span className="nav-label">{label.split(' - ')[0]}</span>}
                                </NavLink>
                            </div>
                        )
                    })}
                </div>

                <div className="sidebar-toggle">
                    <button onClick={toggleSidebar} className="toggle-btn" title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
                        {isCollapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
                    </button>
                </div>
            </aside>

            <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}>
                {isAdmin() && !isSuperAdmin() && subscriptionStatus?.status === 'past_due' && (
                    <div style={{
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffa500',
                        borderRadius: '6px',
                        padding: '12px 20px',
                        margin: '16px 16px 0 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <span style={{ fontSize: '18px' }}>⚠️</span>
                        <span style={{ color: '#856404', fontSize: '14px' }}>
                            <strong>Payment overdue.</strong> Please update your payment method to avoid losing access.
                            {subscriptionStatus.grace_period_end && (
                                <> Your account will be suspended on <strong>{new Date(subscriptionStatus.grace_period_end).toLocaleDateString('en-AU')}</strong>.</>
                            )}
                            {' '}<a href="/subscriptions" style={{ color: '#856404', fontWeight: 'bold' }}>Update payment</a>
                        </span>
                    </div>
                )}
                {children}
            </main>

        </>
    )
}

export default SidebarLayout
